import { Button, Tooltip, useTheme } from '@mui/material';
import { formatDate } from '@strength/utils';
import { MdCheckCircle, MdError, MdWarning } from '@teamworksdev/icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAmsSync, WorkoutToSync } from '../../hooks/useAmsSync';
import { getLocale } from '../../utils/i18n';
import { getAmsSyncStatusIconKind } from './ams-sync-status';

const SYNC_THROTTLE_TIME = 3000;

export function AmsSyncHeader({
  disabled,
  workouts,
}: {
  disabled: boolean;
  workouts: WorkoutToSync[];
}) {
  const {
    palette: { error, success, warning },
  } = useTheme();
  const { t } = useTranslation();
  const { syncAmsWorkouts, amsSyncState } = useAmsSync(workouts);
  const { type } = amsSyncState;
  const lastClickTimeRef = useRef<number>(0);
  const [isThrottled, setIsThrottled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSyncClick = () => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;

    if (timeSinceLastClick < SYNC_THROTTLE_TIME) {
      // Less than SYNC_THROTTLE_TIME since last click, ignore
      return;
    }

    lastClickTimeRef.current = now;
    setIsThrottled(true);
    syncAmsWorkouts();

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset throttled state after SYNC_THROTTLE_TIME
    timeoutRef.current = setTimeout(() => {
      setIsThrottled(false);
      timeoutRef.current = null;
    }, SYNC_THROTTLE_TIME);
  };

  const iconKind = getAmsSyncStatusIconKind(type);

  let chip;
  if (type === 'synced') {
    const lastSynced = amsSyncState.lastSynced?.toISODate();
    const lastSyncedDate = lastSynced
      ? formatDate(lastSynced, getLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : '';
    chip = (
      <Tooltip
        title={t('amsLastSynced', { date: lastSyncedDate })}
        className="flex flex-row items-center gap-1"
      >
        <div>
          <MdCheckCircle aria-hidden="true" color={success.main} size={16} />
        </div>
      </Tooltip>
    );
  } else if (iconKind === 'warning') {
    chip = (
      <Tooltip
        title={t('amsPartiallySynced')}
        className="flex flex-row items-center gap-1"
      >
        <div>
          <MdWarning aria-hidden="true" size={16} color={warning.main} />
        </div>
      </Tooltip>
    );
  } else {
    chip = (
      <Tooltip
        title={t('amsNotSynced')}
        className="flex flex-row items-center gap-1"
      >
        <div>
          <MdError aria-hidden="true" size={16} color={error.main} />
        </div>
      </Tooltip>
    );
  }

  return (
    <div className="flex flex-row items-center gap-1">
      {chip}
      <Button
        className="h-full"
        variant="outlined"
        disabled={disabled || isThrottled}
        onClick={handleSyncClick}
      >
        {t('amsSync')}
      </Button>
    </div>
  );
}
